import { useCloseDeletedSelection } from "../../../components/common/useCloseDeletedSelection";
import CollectionItemActions from "./CollectionItemActions";
import { nextCollectionNumber, useCollectionItems } from "./collectionItems";
import { Form, Button, Card, CardBody, CardFooter, Col, FormControl, FormGroup, FormLabel, Row, Tab, Tabs } from "react-bootstrap";
import { IFieldEditor, ITabItem } from "../../../../models/Interfaces";
import { useEffect, useRef, useState } from "react";
import { createFormValidation } from "../../../../helpers/formValidationHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import HTMLEditor from "../../../components/common/HTMLEditor";
import { tabItemFrame } from "../../../components/common/editorSiteFrame";
import ColourPicker from "../../../components/common/ColourPicker";
import { Core, formValidation, notEmpty, callback } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { SubmitButton } from "@form-validation/plugin-submit-button";

const TabbedContentEditor = (props: IFieldEditor) => {

  const [tabs, setTabs] = useCollectionItems<ITabItem>(props);
  const [ selectedItem, setSelectedItem ] = useState<ITabItem | null>(null);
  const closeDeletedSelection = useCloseDeletedSelection(selectedItem, (item) => item.Id, () => openCloseEditor(false));

  const [ activeTab, setActiveTab ] = useState<string>("");
  const [ editorOpen, setEditorOpen ] = useState<boolean>(false);

  const [editorSession, setEditorSession] = useState(0);
  const formRef = useRef<HTMLFormElement | null>(null);
  const fvRef = useRef<Core | null>(null);

  const editItem = (item: ITabItem | null) : void => {
    setSelectedItem(item);
    openCloseEditor(true);    
  };

  const closeEdit = (): void => {
    openCloseEditor(false);
  }; 

  const addTab = (): void => {
    const tab: ITabItem = {
      Id:  nextCollectionNumber(tabs),
      Content: "",
      Title: "",
      ContentContainer: "",
      TitleClass: "",
      BackgroundColour: ""
    };
    setSelectedItem(tab);
    openCloseEditor(true);
  };

  const updateItemProperty = (name: string, value: string) : void => {
    if (name) {
      setSelectedItem(prev => ({
        ...prev as ITabItem,
        [name]: value
      }));
      if (name) {
        fvRef.current?.revalidateField(name);
      }
    }
  };

  const saveItem = async () : Promise<void> => {
    if (selectedItem && fvRef.current) { 

      const res = await fvRef.current.validate();

      if (res === "Valid") {
        const existing = tabs.find(tab => tab.Id === selectedItem.Id);

        if (!existing) {
          setTabs(prev => [
            ...prev,
            selectedItem
          ]);
        } else {
          const updated = tabs.map(tab => tab.Id === selectedItem.Id ? selectedItem : tab);
          setTabs(updated);
        }
        openCloseEditor(false);
      }
    }
  };

  const createValidation = () : void => {
    if (!fvRef.current && formRef.current) {
      fvRef.current = createFormValidation(formRef.current, {
        fields: {
          Title: {
            validators: {
              callback: {
                callback: (field: HTMLTextAreaElement) => {
                  const validator = { valid: false, message: "A title is required for the tab" };
                  
                  if (field?.value) {
                    validator.valid = true;
                    validator.message = "";
                  }
                  return validator;
                }
              }
            }
          },
          Content: {
            validators: {
              callback: {
                callback: (field: HTMLTextAreaElement) => {
                  const validator = { valid: false, message: "Tab content is required" };
                  
                  if (field?.value) {
                    validator.valid = true;
                    validator.message = "";
                  }
                  return validator;
                }
              }
            }
          }
        },
        plugins: {
          bootstrap5: new Bootstrap5(),
          trigger: new Trigger(),
          submitButton: new SubmitButton(),
        }
      });
    }
  };


  const openCloseEditor = (open: boolean) : void => {
    if (open) setEditorSession(session => session + 1);
    if (props.onEditorOpen) {
      props.onEditorOpen(open);
    }

    if (!open) {
      setEditorOpen(false);
    } else {
      setEditorOpen(true);
    }
  };



  useEffect(() => {
    if (!editorOpen) return;
    createValidation();
    return () => {
      fvRef.current?.destroy();
      fvRef.current = null;
    };
  }, [editorOpen]);


  return (
    <>
      {!editorOpen && (
        <Row>
          <Col xs={12} className="mb-3">
            <Button variant="outline-primary" title="add a tab" onClick={addTab}>
              <FontAwesomeIcon icon={faPlus} />&nbsp;Add Tab
            </Button>
          </Col>
        </Row>
      )}

      <div hidden={editorOpen}>
        <Tabs
          activeKey={activeTab}
          onSelect={(tk) => setActiveTab(tk ?? `TAB-${tabs[0]?.Id ?? ""}`)}>
          {tabs.map((t, index) => (
            <Tab 
              key={t.Id}  
              eventKey={`TAB-${t.Id}`}
              title={<span dangerouslySetInnerHTML={{__html: t.Title}}></span>}
              className={t.TitleClass ?? ""}
            >
              <Card className="h-100 rounded-0">
                <CardBody className="rounded-0">
                  <div dangerouslySetInnerHTML={{__html: t.Content }}></div>
                </CardBody>
                <CardFooter>
                  <Button 
                    variant={"outline-secondary"} 
                    className="btn-nomin me-1" 
                    title="edit tab"
                    onClick={() => editItem(t)}>
                      <FontAwesomeIcon icon={faEdit} />
                  </Button>
                  <Button
                    variant={"outline-secondary"}
                    className="btn-nomin"
                    title="remove tab" onClick={() => { setTabs(tabs.filter(item => item.Id !== t.Id)); closeDeletedSelection(t.Id); setActiveTab(`TAB-${tabs.find(item => item.Id !== t.Id)?.Id ?? ""}`); }}>
                      <FontAwesomeIcon icon={faTimes} />
                  </Button>
                  <CollectionItemActions items={tabs} index={index} onChange={setTabs} />
                </CardFooter>
              </Card>
            </Tab>
          ))}
        </Tabs>
      </div>
      <div hidden={!editorOpen} data-nested-editor-open={editorOpen ? "true" : undefined}>
        {selectedItem !== null && (
          <Form id="TabItemForm" ref={formRef}>
            <Row>
              <Col md={12} className="mt-3">
                <FormGroup className="form-group">
                  <FormLabel>Tab Title</FormLabel>
                  <HTMLEditor documentKey={editorSession} height={props.editorHeight}
                    id="Title" 
                    name="Title" 
                    value={selectedItem?.Title ?? ""} 
                    siteFrame={tabItemFrame(props.siteFrame, "title", selectedItem ?? { TitleClass: "" })}
                    onChange={(content) => updateItemProperty("Title", content)} 
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12} className="mt-3">
                <FormGroup className="form-group">
                  <FormLabel>Tab Content</FormLabel>
                  <HTMLEditor documentKey={editorSession} height={props.editorHeight}
                    id="Content" 
                    name="Content" 
                    value={selectedItem?.Content ?? ""} 
                    siteFrame={tabItemFrame(props.siteFrame, "content", selectedItem ?? { ContentContainer: "", BackgroundColour: "" })}
                    onChange={(content) => updateItemProperty("Content", content)} 
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12} xl={6} className="mt-3">
                <FormGroup className="form-group">
                  <FormLabel>Content Container Class</FormLabel>
                  <FormControl
                    type="text"
                    name="ContentContainer"
                    title="add a bootstrap or your own css class"
                    value={selectedItem?.ContentContainer ?? ""}
                    placeholder="add a bootstrap or your own css class..."
                    onChange={(e) => updateItemProperty(e.target.name, e.target.value)}>
                  </FormControl>
                </FormGroup>
              </Col>
              <Col md={12} xl={6} className="mt-3">
                <FormGroup>
                  <FormLabel>Tab Title Class</FormLabel>
                  <FormControl
                    type="text"
                    name="TitleClass"
                    title="add a bootstrap or your own css class"
                    value={selectedItem?.TitleClass ?? ""}
                    placeholder="add a bootstrap or your own css class..."
                    onChange={(e) => updateItemProperty(e.target.name, e.target.value)}>
                  </FormControl>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12} xl={6} className="mt-3">
                <FormGroup>
                  <FormLabel>Content Background Colour</FormLabel>
                  <ColourPicker 
                    selectedColour={selectedItem?.BackgroundColour ?? ""} 
                    onColourChange={(colour: string | null) => updateItemProperty("BackgroundColour", colour ?? "")} 
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <hr />
              </Col>
            </Row>
            <Row>
              <Col xs={12} className="text-end">
                <Button 
                  variant="outline-success" 
                  className="me-1" 
                  title="Save tab details"
                  onClick={saveItem}>
                  <FontAwesomeIcon icon={faSave} />&nbsp;Save
                </Button>
                <Button 
                  variant="outline-primary" 
                  onClick={closeEdit} 
                  title="Close item editor">Close</Button>
              </Col>
            </Row>
          </Form>
        )}
      </div>
    </>
  );
}

export default TabbedContentEditor;
